import { useQuery } from '@tanstack/react-query';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from 'src/components/Dialog';
import { Skeleton } from 'src/components/Skeleton';
import { Stack } from 'src/components/Stack';
import { fetchUser } from 'src/models/user/user';
import { Visit } from 'src/models/visit/_types';
import PaymentMethodsTab from 'src/pages/Clients/PaymentsPage/PaymentMethodsTab/PaymentMethodsTab';

interface VisitAddCardDialogProps {
  open: boolean;
  onClose: () => void;
  visit: Visit | null;
}

/**
 * Dialog for adding/updating payment method for a visit's user.
 * Uses the existing PaymentMethodsTab component.
 */
export const VisitAddCardDialog: FC<VisitAddCardDialogProps> = ({
  open,
  onClose,
  visit,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'calendar.viewEventDialog.visitAddCardDialog' });
  // Fetch full user data since visit only has userId
  const { data: user, isPending } = useQuery({
    queryKey: ['user', visit?.userId],
    queryFn: () => fetchUser({ id: visit?.userId || '' }),
    enabled: !!visit?.userId && open,
  });

  const userName = visit?.userName || 'User';
  const title = visit?.userCardOnFile
    ? t('updateCardFor', { userName })
    : t('addCardFor', { userName });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      maxWidth="sm"
      fullWidth
    >
      {isPending ? (
        <Stack spacing={2} sx={{ p: 2 }}>
          <Skeleton height={60} />
          <Skeleton height={60} />
          <Skeleton height={40} />
        </Stack>
      ) : user ? (
        <PaymentMethodsTab user={user} />
      ) : (
        <Stack sx={{ p: 2 }}>
          {t('unableToLoadUser')}
        </Stack>
      )}
    </Dialog>
  );
};

